import { useState, type FormEvent } from "react";
import { Aviso, Botao, Campo, CampoLongo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  ROTULO_DO_DISPARO,
  ROTULO_DO_TIPO_DE_PESQUISA,
  TIPOS_DE_PESQUISA,
  usePesquisas,
  useSalvarPesquisa,
  validarPesquisa,
  type TipoDePesquisa,
} from "../../nucleo/pesquisas";
import { useVoltarParaHome } from "../../nucleo/voltarParaHome";

const PERGUNTA_PADRAO: Record<TipoDePesquisa, string> = {
  nps: "Em uma escala de 0 a 10, qual a chance de você recomendar o atendimento da GEOvendas a um colega?",
  ces: "De 1 (muito difícil) a 7 (muito fácil), quão fácil foi resolver o seu problema?",
};

/** As pesquisas configuradas e o formulário para criar uma nova. */
export function PainelDeConfiguracao() {
  const pesquisas = usePesquisas();
  const salvar = useSalvarPesquisa();
  const voltarParaHome = useVoltarParaHome();
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState<TipoDePesquisa>("nps");
  const [pergunta, setPergunta] = useState(PERGUNTA_PADRAO.nps);
  const [disparo, setDisparo] = useState("resolvido");
  const [problema, setProblema] = useState<string | null>(null);

  function trocarTipo(novo: TipoDePesquisa) {
    // Só troca a pergunta sugerida quando a pessoa não a editou.
    if (pergunta === PERGUNTA_PADRAO[tipo]) setPergunta(PERGUNTA_PADRAO[novo]);
    setTipo(novo);
  }

  function criar(evento: FormEvent) {
    evento.preventDefault();
    const erro = validarPesquisa({ nome, pergunta });
    setProblema(erro);
    if (erro) return;
    salvar.mutate(
      { corpo: { nome: nome.trim(), tipo, pergunta: pergunta.trim(), disparo_em: disparo } },
      {
        onSuccess: () => {
          setNome("");
          voltarParaHome("Pesquisa criada.");
        },
      },
    );
  }

  return (
    <div data-testid="painel-de-configuracao">
      <Cartao titulo="Nova pesquisa" className="mb-4">
        <form onSubmit={criar} noValidate data-testid="formulario-de-pesquisa">
          <div className="grid gap-3 md:grid-cols-3">
            <Campo rotulo="Nome" obrigatorio value={nome} onChange={(e) => setNome(e.target.value)} />
            <div className="campo">
              <label className="campo__rotulo" htmlFor="tipo-da-pesquisa">
                Tipo
              </label>
              <select
                id="tipo-da-pesquisa"
                className="campo__controle"
                value={tipo}
                onChange={(e) => trocarTipo(e.target.value as TipoDePesquisa)}
              >
                {TIPOS_DE_PESQUISA.map((item) => (
                  <option key={item} value={item}>
                    {ROTULO_DO_TIPO_DE_PESQUISA[item]}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label className="campo__rotulo" htmlFor="disparo-da-pesquisa">
                Quando enviar
              </label>
              <select
                id="disparo-da-pesquisa"
                className="campo__controle"
                value={disparo}
                onChange={(e) => setDisparo(e.target.value)}
              >
                {Object.entries(ROTULO_DO_DISPARO).map(([valor, rotulo]) => (
                  <option key={valor} value={valor}>
                    {rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <CampoLongo
            rotulo="Pergunta"
            obrigatorio
            value={pergunta}
            onChange={(e) => setPergunta(e.target.value)}
          />
          {problema ? <Aviso tom="erro" className="mb-3">{problema}</Aviso> : null}
          {salvar.isError ? <Aviso tom="erro" className="mb-3">{mensagemDoConsole(salvar.error)}</Aviso> : null}
          <Botao type="submit" disabled={salvar.isPending} data-testid="criar-pesquisa">
            {salvar.isPending ? "Criando…" : "Criar pesquisa"}
          </Botao>
        </form>
      </Cartao>

      <h2 className="titulo-secao">Pesquisas</h2>
      {pesquisas.isPending ? <p role="status">Carregando pesquisas…</p> : null}
      {pesquisas.isError ? <Aviso tom="erro">{mensagemDoConsole(pesquisas.error)}</Aviso> : null}
      {pesquisas.data && pesquisas.data.length === 0 ? (
        <Aviso tom="marca">Nenhuma pesquisa configurada. Crie a primeira acima.</Aviso>
      ) : null}
      <div className="lista-de-itens">
        {pesquisas.data?.map((pesquisa) => (
          <Cartao
            key={pesquisa.id}
            titulo={pesquisa.nome}
            data-testid={`pesquisa-${pesquisa.nome}`}
            acessorio={<Chip estado={pesquisa.ativa ? "ok" : "neutro"}>{pesquisa.ativa ? "Ativa" : "Desligada"}</Chip>}
          >
            <p className="mb-2">{pesquisa.pergunta}</p>
            <p className="nota-rodape mb-2">
              {ROTULO_DO_TIPO_DE_PESQUISA[pesquisa.tipo]} · {ROTULO_DO_DISPARO[pesquisa.disparo_em]} ·{" "}
              {pesquisa.enviados} enviada(s), {pesquisa.respondidos} respondida(s)
            </p>
            <Botao
              tamanho="pequeno"
              variante="fantasma"
              aria-label={`${pesquisa.ativa ? "Desligar" : "Ligar"} a pesquisa ${pesquisa.nome}`}
              disabled={salvar.isPending}
              onClick={() => salvar.mutate({ id: pesquisa.id, corpo: { ativa: !pesquisa.ativa } })}
            >
              {pesquisa.ativa ? "Desligar" : "Ligar"}
            </Botao>
          </Cartao>
        ))}
      </div>
    </div>
  );
}
